import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill, type StatusVariant } from '../patterns';
import { formatQty, selectDisplayUnit, convertToUnit } from '../../utils/chemicalStockUsageMath';
import type { EnrichedChemicalStockUsageRow, StockStatus } from '../../utils/chemicalStockUsageMath';

export interface ForecastVsStockMeterProps {
  row: EnrichedChemicalStockUsageRow;
}

const STOCK_VARIANT: Record<StockStatus, StatusVariant> = {
  sufficient:   'ok',
  low:          'warn',
  insufficient: 'danger',
  conflict:     'warn',
};

const STOCK_LABEL_KEY: Record<StockStatus, string> = {
  sufficient:   'spray.dashboard.chemicalStockUsage.status.sufficient',
  low:          'spray.dashboard.chemicalStockUsage.status.low',
  insufficient: 'spray.dashboard.chemicalStockUsage.status.insufficient',
  conflict:     'inventory.unitConflict.badge',
};

export const ForecastVsStockMeter: React.FC<ForecastVsStockMeterProps> = ({ row }) => {
  const { t } = useTranslation();

  // A unit-conflict row (stock: null) has no reliable on-hand figure — fall
  // back to 0 for the bar/unit math (never NaN) but suppress the on-hand
  // number + coverage subline and render the review pill instead below.
  const isConflict = row.stock == null;
  const stockVal    = row.stock ?? 0;

  // Pick one display unit for the whole meter so the two sides + the breakdown
  // subline + the bar/pin labels all read in the same unit. Decision is based
  // on the largest non-zero value across the meter — see selectDisplayUnit.
  const dUnit     = selectDisplayUnit([row.forecast, stockVal, row.draft, row.planned], row.unit);
  const dForecast = convertToUnit(row.forecast, row.unit, dUnit);
  const dStock    = convertToUnit(stockVal,     row.unit, dUnit);
  const dDraft    = convertToUnit(row.draft,    row.unit, dUnit);
  const dPlanned  = convertToUnit(row.planned,  row.unit, dUnit);

  const max       = Math.max(row.forecast, stockVal) * 1.05 || 1;
  const forecastW = (row.forecast / max) * 100;
  const stockW    = isConflict ? 0 : (stockVal / max) * 100;
  const variant   = STOCK_VARIANT[row.stockStatus];
  const pillLabel = t(STOCK_LABEL_KEY[row.stockStatus]);

  return (
    <div className="csu-meter csu-meter--forecast">
      <div className="csu-meter-numbers">
        <div className="csu-num">
          <span className="csu-num-label">{t('spray.dashboard.chemicalStockUsage.label.forecast')}</span>
          <span className="csu-num-val">
            {formatQty(dForecast)} <span className="csu-num-unit">{dUnit}</span>
          </span>
          <span className="csu-num-sub">
            {t('spray.dashboard.chemicalStockUsage.label.draftPlannedBreakdown', {
              draft: formatQty(dDraft),
              planned: formatQty(dPlanned),
            })}
          </span>
        </div>
        <div className="csu-num">
          <span className="csu-num-label">{t('spray.dashboard.chemicalStockUsage.label.onHand')}</span>
          <span className="csu-num-val">
            {isConflict ? '—' : (<>{formatQty(dStock)} <span className="csu-num-unit">{dUnit}</span></>)}
          </span>
          {!isConflict && (
            <span className="csu-num-sub">
              {t('spray.dashboard.chemicalStockUsage.label.coverage', { pct: Math.round(row.coveragePct * 100) })}
            </span>
          )}
        </div>
        <span className={`csu-pill-with-dot csu-pill-with-dot--${row.stockStatus}`}>
          <StatusPill variant={variant}>{pillLabel}</StatusPill>
        </span>
      </div>
      <div className="csu-meter-track">
        <div className="csu-meter-forecast-band" style={{ width: `${forecastW}%` }} />
        <div className={`csu-meter-stock csu-meter-stock--${row.stockStatus}`} style={{ width: `${stockW}%` }}>
          <span className="csu-meter-stock-label">{isConflict ? '—' : formatQty(dStock)}</span>
        </div>
        <div className="csu-meter-pin" style={{ left: `${forecastW}%` }}>
          <span className="csu-meter-pin-line" />
          <span className="csu-meter-pin-label">
            {t('spray.dashboard.chemicalStockUsage.label.forecast')} {formatQty(dForecast)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ForecastVsStockMeter;
